import React, { useState, useEffect } from 'react';
import { collection, getDocs, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { dbFirestore } from '../lib/firebase';
import { db } from '../lib/database';
import { MOCK_USERS } from '../constants';
import { Database, RefreshCw, CheckCircle, XCircle, Trash2, Plus, Eye } from 'lucide-react';

export const DebugDB: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev]);
    console.log(message);
  };

  const checkConnection = async () => {
    setConnectionStatus('checking');
    addLog('🔍 Đang kiểm tra kết nối Firebase...');
    
    try {
      const usersRef = collection(dbFirestore, 'users');
      const snapshot = await getDocs(usersRef);
      
      if (snapshot) {
        setConnectionStatus('connected');
        addLog(`✅ Kết nối Firebase thành công!`);
        addLog(`📊 Tìm thấy ${snapshot.size} users trong database`);
        
        const usersList: any[] = [];
        snapshot.forEach((doc) => {
          usersList.push({ id: doc.id, ...doc.data() });
        });
        setUsers(usersList);
        
        return true;
      }
    } catch (error: any) {
      setConnectionStatus('error');
      addLog(`❌ Lỗi kết nối Firebase: ${error.message}`);
      console.error('Firebase Error:', error);
      return false;
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    addLog('📥 Đang tải users từ Firebase...');
    
    try {
      const usersRef = collection(dbFirestore, 'users');
      const snapshot = await getDocs(usersRef);
      const usersList: any[] = [];
      
      snapshot.forEach((doc) => {
        usersList.push({ id: doc.id, ...doc.data() });
      });
      
      setUsers(usersList);
      addLog(`✅ Đã load ${usersList.length} users`);
    } catch (error: any) {
      addLog(`❌ Lỗi khi load users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const seedAdminUser = async () => {
    setLoading(true);
    addLog('🌱 Đang tạo admin user...');
    
    try {
      const adminUser = MOCK_USERS[0];
      await setDoc(doc(dbFirestore, 'users', adminUser.id), adminUser);
      addLog(`✅ Đã tạo admin user thành công!`);
      addLog(`   Email: ${adminUser.email}`);
      addLog(`   Password: ${adminUser.password}`);
      await loadUsers();
    } catch (error: any) {
      addLog(`❌ Lỗi khi tạo admin user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const forceUpdateAdmin = async () => {
    setLoading(true);
    addLog('🔄 Đang force update admin user...');
    
    try {
      await db.forceUpdateAdmin();
      addLog(`✅ Đã update admin user thành công!`);
      await loadUsers();
    } catch (error: any) {
      addLog(`❌ Lỗi khi update admin: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteAllUsers = async () => {
    if (!window.confirm('⚠️ BẠN CÓ CHẮC CHẮN MUỐN XÓA TẤT CẢ USERS KHÔNG? Hành động này không thể hoàn tác!')) {
      return;
    }
    
    setLoading(true);
    addLog('🗑️ Đang xóa tất cả users...');
    
    try {
      const usersRef = collection(dbFirestore, 'users');
      const snapshot = await getDocs(usersRef);
      
      for (const docSnap of snapshot.docs) {
        await deleteDoc(doc(dbFirestore, 'users', docSnap.id));
        addLog(`🗑️ Đã xóa user: ${docSnap.id}`);
      }
      
      addLog(`✅ Đã xóa ${snapshot.size} users`);
      await loadUsers();
    } catch (error: any) {
      addLog(`❌ Lỗi khi xóa users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-8 h-8 text-cyan-400" />
            <div>
              <h1 className="text-3xl font-bold">Firebase Database Debug</h1>
              <p className="text-slate-400 text-sm">Kiểm tra kết nối và dữ liệu Firebase Firestore</p>
            </div>
          </div>
          
          {/* Connection Status */}
          <div className="flex items-center gap-3 p-4 bg-slate-900 rounded-lg">
            {connectionStatus === 'checking' && (
              <>
                <RefreshCw className="w-5 h-5 animate-spin text-yellow-400" />
                <span className="text-yellow-400">Đang kiểm tra kết nối...</span>
              </>
            )}
            {connectionStatus === 'connected' && (
              <>
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-bold">Kết nối Firebase thành công!</span>
              </>
            )}
            {connectionStatus === 'error' && (
              <>
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-bold">Lỗi kết nối Firebase!</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold mb-4">Thao tác Database</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={loadUsers}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh Users
            </button>
            
            <button
              onClick={seedAdminUser}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
            >
              <Plus className="w-5 h-5" />
              Tạo Admin User
            </button>
            
            <button
              onClick={forceUpdateAdmin}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-500 px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
            >
              <RefreshCw className="w-5 h-5" />
              Update Admin
            </button>
            
            <button
              onClick={deleteAllUsers}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
              Xóa Tất Cả
            </button>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Danh sách Users ({users.length})</h2>
            <button
              onClick={loadUsers}
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          {users.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Không có users trong database</p>
              <button
                onClick={seedAdminUser}
                className="mt-4 bg-green-600 hover:bg-green-500 px-6 py-2 rounded-lg font-bold transition-all"
              >
                Tạo Admin User Ngay
              </button>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {users.map((user) => (
                <div key={user.id} className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">ID:</span>
                      <span className="ml-2 font-mono text-cyan-400">{user.id}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <span className="ml-2 font-bold">{user.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <span className="ml-2 text-green-400">{user.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Password:</span>
                      <span className="ml-2 font-mono text-yellow-400">{user.password || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Role:</span>
                      <span className={`ml-2 font-bold ${user.role === 'ADMIN' ? 'text-red-400' : 'text-blue-400'}`}>
                        {user.role}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Status:</span>
                      <span className="ml-2">{user.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold mb-4">Activity Logs</h2>
          <div className="bg-black rounded-lg p-4 font-mono text-xs space-y-1 max-h-64 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-slate-600">Chưa có logs...</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="text-slate-300">{log}</div>
              ))
            )}
          </div>
        </div>

        {/* Firebase Config Info */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold mb-4">Firebase Configuration</h2>
          <div className="bg-black rounded-lg p-4 font-mono text-xs text-slate-400">
            <div>Project ID: signalviet-19fbe</div>
            <div>Auth Domain: signalviet-19fbe.firebaseapp.com</div>
            <div>Database: Firestore</div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-bold transition-all"
          >
            ← Quay lại trang chủ
          </a>
        </div>
      </div>
    </div>
  );
};
